// Content for the fork diagram: Bg 7.16, 18.56; SB 3.16.13, 3.29.13, 11.11.29-32, 11.20.6-11

import type { TopicContent } from '../types';
import { FORK_DIAGRAM_SVG_MARKUP } from '../assets/fork-diagram-svg';

const content: TopicContent = {
    blocks: [
        {
            type: 'diagram',
            diagram: {
                svgMarkup: FORK_DIAGRAM_SVG_MARKUP,
                zoomable: true,
            },
        },
        {
            type: 'text',
            content:
                'This chart traces one teaching of Śrīla Viśvanātha Cakravartī Ṭhākura through the Bhagavad-gītā and Śrīmad-Bhāgavatam. The sections below walk through it step by step — each heading names the exact turn in the chart it covers, with the śloka reference in brackets.',
        },
        {
            type: 'heading',
            headingText: 'The Verse That Maps the Whole Chart (Bg 7.16)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 7.16',
                sloka: `catur-vidhā bhajante māṁ
janāḥ su-kṛtino 'rjuna
ārto jijñāsur arthārthī
jñānī ca bharatarṣabha`,
                translation:
                    'Four types (<strong>catur-vidhā</strong>) of religious (<strong>su-kṛtino</strong>) persons (<strong>janāḥ</strong>) worship Me (<strong>māṁ bhajante</strong>), O Arjuna (<strong>arjuna</strong>): the sufferer (<strong>ārto</strong>), the inquirer (<strong>jijñāsur</strong>), the enjoyer (<strong>arthārthī</strong>) and the <em>jñānī</em> (<strong>jñānī ca</strong>), O best of Bharata (<strong>bharatarṣabha</strong>).',
            },
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"These four types of devotees, qualified for pradhānī bhūtā bhakti (in which bhakti is the principle component), have been enumerated. The first three types of persons have karma-miśra-bhakti. The fourth type has jñāna-miśra-bhakti."</p></div>',
        },
        {
            type: 'text',
            content: 'He then gives the whole entry fork in a single sentence:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"The result of sakāma-karma is Svarga, the result of niṣkāma-karma is jñāna-yoga, and the result of jñāna and aṣṭāṅga-yoga is nirvāṇa mokṣa, impersonal liberation."</p></div>',
        },
        {
            type: 'text',
            content:
                'For the karma-miśra-bhakti of the first three worshippers, he adds something the chart doesn\'t currently show — that this particular sālokya doesn\'t carry the risk ordinary Svarga does:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"The results of the two types of pradhānī bhūtā bhakti are described as follows. Among the types of pradhānī bhūtā bhakti, the first three are karma-miśra-bhakti, or sakāma-bhakti. The result of this bhakti is attainment of the respective desires (deliverance from suffering, attainment of scriptural knowledge with materialistic method, gain of material benefits). And after that, because the superior nature of their object of worship (the Lord), those devotees attain liberation in the form of sālokya (attaining the same planet) with predominance of happiness and powers (sukha and aiśvarya). And there is no fall down as in the case of exhausting enjoyment in Svarga as a result of karma."</p></div>',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"The result of jñāna-miśra-bhakti, which is superior to the other three types, is śānta rati (bhāva), as in the case of Sanaka and others. Sometimes, because of exceptional mercy of the Lord and His devotee, the result of jñāna-miśra-bhakti is the supreme position of prema, as in the case of Śukadeva."</p></div>',
        },
        {
            type: 'text',
            content:
                'Sanaka is one of the Four Kumāras, the same example this chart uses for Śānta-rati, named here independently of the Jaya-Vijaya story. This passage also explains the two dotted, exceptional arrows — a shortcut straight to prema that bypasses the usual outcome:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"If sakāma-bhakti (the first three types) becomes niṣkāma, without desires, the result is jñāna-miśra-bhakti, and the result of that jñāna-miśra-bhakti is as stated above (śānta-rati). Sometimes, those who have jñāna-miśra-bhakti or karma-miśra-bhakti attain prema in dāsya and higher rasas on their own (with no apparent association of devotees in this life), because of influence of association with devotees in previous lives. However, that prema is predominated by a mood of reverence (aiśvarya)."</p></div>',
        },
        {
            type: 'text',
            content: 'And finally, the destination at the far right of the chart, in the same breath:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 7.16</div><p class="italic text-black dark:text-gray-100">"The result of pure bhakti unmixed with jñāna, karma or yoga, called ananyā bhakti akiñcanā bhakti or uttamā bhakti, which has many types, is that one becomes an associate of the Lord with dāsya, sākhya, and other rasas."</p></div>',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'Niṣkāma Karma Yoga → Jñāna Yoga, or Niṣkāma Karma Miśra Bhakti (SB 11.20.6-11)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.6',
                sloka: `yogās trayo mayā proktā
nṝṇāṁ śreyo-vidhitsayā
jñānaṁ karma ca bhaktiś ca
nopāyo 'nyo 'sti kutracit`,
                translation:
                    'The Supreme Lord said: Because I desire that human beings may achieve perfection (<strong>nṝṇāṁ śreyaḥ-vidhitsayā</strong>), I have presented three methods (<strong>yogāḥ trayaḥ mayā proktāḥ</strong>) — the path of <em>jñāna</em> (<strong>jñānam</strong>), the path of <em>karma</em> (<strong>karma ca</strong>) and the path of <em>bhakti</em> (<strong>bhaktiḥ ca</strong>). Besides these three, there no other means of elevation (<strong>na upāyaḥ anyaḥ asti kutracit</strong>).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.7',
                sloka: `nirviṇṇānāṁ jñāna-yogo
nyāsinām iha karmasu
teṣv anirviṇṇa-cittānāṁ
karma-yogas tu kāminām`,
                translation:
                    '<em>Jñāna-yoga</em> (<strong>jñāna-yogaḥ</strong>) is recommended for those who are unattached to material life (<strong>nirviṇṇānām</strong>) and thus reject material activities (<strong>nyāsinām iha karmasu</strong>). <em>Karma-yoga</em> (<strong>karma-yogaḥ tu</strong>) is recommended for those who are not disgusted with material life (<strong>teṣv anirviṇṇa-cittānāṁ</strong>) and have great attachment to pleasures (<strong>kāminām</strong>).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.8',
                sloka: `yadṛcchayā mat-kathādau
jāta-śraddhas tu yaḥ pumān
na nirviṇṇo nāti-sakto
bhakti-yogo 'sya siddhi-daḥ`,
                translation:
                    'If by unexpected association with devotees (<strong>yadṛcchayā</strong>) one develops faith (<strong>jāta-śraddhaḥ tu</strong>) in my topics (<strong>mat-kathā-ādau</strong>), that person (<strong>yaḥ pumān</strong>), being neither very disgusted with (<strong>na nirviṇṇaḥ</strong>) nor attached to material life (<strong>na ati-saktaḥ</strong>), is qualified for <em>bhakti</em> (<strong>bhakti-yogaḥ</strong>) and will achieve perfection (<strong>asya siddhi-daḥ</strong>).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.11',
                sloka: `asmiḹ loke vartamānaḥ
sva-dharma-stho 'naghaḥ śuciḥ
jñānaṁ viśuddham āpnoti
mad-bhaktiṁ vā yadṛcchayā`,
                translation:
                    'One who is situated in his prescribed duty (<strong>sva-dharma-sthaḥ</strong>), free from sinful activities (<strong>anaghaḥ</strong>) and cleansed of material contamination (<strong>śuciḥ</strong>), in this very life (<strong>asmin loke vartamānaḥ</strong>) obtains transcendental knowledge (<strong>jñānam viśuddham āpnoti</strong>) or, by fortune, <em>bhakti</em> to me (<strong>mat-bhaktim vā yadṛcchayā</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Uddhava has just asked how liberation is possible if the Vedas are full of orders and prohibitions. Kṛṣṇa\'s answer is that there are three separate paths, each with its own qualification, and each caused by something different.',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 11.20.6</div><p class="italic text-black dark:text-gray-100">"Karma is for the karmīs and jñāna is for the jñānīs. They are not qualified for pure bhakti."</p></div>',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 11.20.7</div><p class="italic text-black dark:text-gray-100">"Jñāna is for persons who are unattached to house and family and therefore renounce material actions of household life. Karma is for those who are not detached from material action because they are extremely attached to enjoying their body, house, and wife."</p></div>',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 11.20.8</div><p class="italic text-black dark:text-gray-100">"The qualification for the three processes should be considered. The cause of disgust with or detachment from the world is purification of the antaḥ-karaṇa caused by niṣkāma-karma. The cause of excessive attachment is beginningless ignorance. The cause of being devoid of excessive attachment is unexpected association with great devotees."</p></div>',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 11.20.11</div><p class="italic text-black dark:text-gray-100">"What does this performer of karma attain? Situated on this planet, performing his duties, sinless because of performing niṣkāma-karma, pure in heart, because of no sin, he attains jñāna and liberation. If he unexpectedly attains the association with persons practicing pure bhakti, he attains pure bhakti to me. Then he attains prema. If he attains association with karma-miśra-bhaktas or jñāna-miśra-bhaktas, by that type of bhakti he attains śānti-rati."</p></div>',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'Niṣkāma Karma Miśra Bhakti → Vaikuṇṭha-type Mukti (Bg 18.56, SB 3.29.13)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 18.56',
                sloka: `sarva-karmāṇy api sadā
kurvāṇo mad-vyapāśrayaḥ
mat-prasādād avāpnoti
śāśvataṁ padam avyayam`,
                translation:
                    'But that person, who, though performing all types of actions (<strong>sarva-karmāṇy api sadā kurvāṇo</strong>), also takes shelter of Me predominantly (<strong>mad-vyapāśrayaḥ</strong>), attains My indestructible, eternal abode (<strong>śāśvataṁ padam avyayam</strong>), by My mercy (<strong>mat-prasādād avāpnoti</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Kṛṣṇa is describing the devotee who attains His own eternal abodes — Vaikuṇṭha, Mathurā, Dvārakā, Ayodhyā. Śrīla Viśvanātha Cakravartī Ṭhākura is explicit about who this devotee is <em>not</em>:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 18.56</div><p class="italic text-black dark:text-gray-100">"That devotee takes shelter of Me, even with evident material desires... He is not even a niṣkāma-bhakta, for he performs all actions even material ones such as protecting wife and children as well as nitya, naimittika and kāmya-karmas."</p></div>',
        },
        {
            type: 'text',
            content: 'He continues, naming exactly what this devotee has and where it takes him:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on Bg 18.56</div><p class="italic text-black dark:text-gray-100">"Thus, he has bhakti mixed with karma, rather than karma mixed with bhakti, which was already described in the first six chapters. There is no overlapping of actions with the previous descriptions. That bhakta attains My eternal dhāmas (padam), such as Vaikuṇṭha, Mathurā, Dvārakā, and Ayodhyā."</p></div>',
        },
        {
            type: 'text',
            content:
                'Now, the four liberations that make up Vaikuṇṭha-type mukti. Kapiladeva names them in his teaching to Devahūti:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.29.13',
                sloka: `sālokya-sārṣṭi-sāmīpya-
sārūpyaikatvam apy uta
dīyamānaṁ na gṛhṇanti
vinā mat-sevanaṁ janāḥ`,
                translation:
                    'My devotees (<strong>janāḥ</strong>) do not accept (<strong>na gṛhṇanti</strong>) living on my planet (<strong>sālokya</strong>), having similar powers (<strong>sārṣṭi</strong>), staying close to me (<strong>sāmīpya</strong>), or having a similar form (<strong>sārūpya</strong>), what to speak of merging into me (<strong>ekatvam apy uta</strong>), when these are offered (<strong>dīyamānaṁ</strong>), unless it involves service to me (<strong>vinā mat-sevanaṁ</strong>).',
            },
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 3.29.13</div><p class="italic text-black dark:text-gray-100">"Sālokya means living on the same planet as the Lord. Sārṣṭi means having powers similar to the Lord\'s. Sāmīpya means staying near the Lord. Sārūpya means having a form similar to the Lord\'s. Ekatvam means merging with the Lord."</p></div>',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'Śānta-rati in Vaikuṇṭha → Prema: Why the Four Kumāras Stopped Short (SB 3.16.13)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.16.13',
                sloka: `atha tasyośatīṁ devīm
ṛṣi-kulyāṁ sarasvatīm
nāsvādya manyu-daṣṭānāṁ
teṣām ātmāpy atṛpyata`,
                translation:
                    'Brahmā said: Though hearing the pleasing, logical and worshipable words of the Lord (<strong>atha tasyośatīṁ devīm ṛṣi-kulyāṁ sarasvatīm</strong>), the Kumāras were still not satisfied (<strong>teṣām ātmāpy atṛpyata</strong>), since their minds were bitten by the snake of anger (<strong>manyu-daṣṭānāṁ nāsvādya</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'The Four Kumāras had cursed Jaya and Vijaya, the Lord\'s own gatekeepers, in anger. Śrīla Viśvanātha Cakravartī Ṭhākura reads their unfinished repentance as the reason their devotion stopped exactly here:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 3.16.13</div><p class="italic text-black dark:text-gray-100">"If the Kumāras had not committed offense to the devotees, then they would have developed pure dāsya-prema, from the mercy of the devotees and the Lord. But because of the remaining presence of offense, they developed only to the stage of śānta-bhakti."</p></div>',
        },
        {
            type: 'text',
            content: 'He then quotes the rule this follows, from Bhakti-rasāmṛta-sindhu:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhakti-rasāmṛta-sindhu 1.3.54 (quoted in the commentary on SB 3.16.13)',
                sloka: `bhāvo 'py abhāvam āyāti
kṛṣṇa-preṣṭhāparādhataḥ
ābhāsatāṁ ca śanakair
nyūna-jātīyatām api`,
                translation:
                    'By an offense against the dearest devotee of the Lord, even real <em>bhāva</em> will be destroyed, if the offense is grave. If the offense is medium, the <em>bhāva</em> will turn to <em>bhāvābhāsa</em>. If the offense is slight, the <em>bhāva</em> will become an inferior type.',
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'What "Śuddha-Bhakti" Actually Means (SB 3.29.11-12, SB 11.11.29-32)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.29.11-12',
                sloka: `mad-guṇa-śruti-mātreṇa
mayi sarva-guhāśaye
mano-gatir avicchinnā
yathā gaṅgāmbhaso 'mbudhau
lakṣaṇaṁ bhakti-yogasya
nirguṇasya hy udāhṛtam
ahaituky avyavahitā
yā bhaktiḥ puruṣottame`,
                translation:
                    'Because the mind, by hearing about my qualities (<strong>mad-guṇa-śruti-mātreṇa</strong>), flows continuously (<strong>mano-gatir avicchinnā</strong>) to me, the Supreme Lord residing in the hearts of all people (<strong>mayi sarva-guhāśaye</strong>), just as the Gaṅgā flows to the ocean (<strong>yathā gaṅgāmbhaso \'mbudhau</strong>), it is said (<strong>hy udāhṛtam</strong>) that the mark (<strong>lakṣaṇaṁ</strong>) of <em>bhakti-yoga</em> beyond the <em>guṇas</em> (<strong>bhakti-yogasya nirguṇasya yā bhaktiḥ puruṣottame</strong>) is that it seeks no result other than itself (<strong>ahaituky</strong>), and admits no obstruction from anything else (<strong>avyavahitā</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'This is Kapiladeva\'s own definition of the pure bhakti at the top of this chart. Śrīla Viśvanātha Cakravartī Ṭhākura reads two words as doing all the work:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 3.29.11-12</div><p class="italic text-black dark:text-gray-100">"It is devoid of having other results in mind (ahaitukī). It is not like jñāna or yoga, since it independent of other processes, and is itself the result... This bhakti is unobstructed (avyavahitā), devoid of blockage by jñāna or karma. This means it is beyond the guṇas."</p></div>',
        },
        {
            type: 'text',
            content:
                'The exact phrase this chart uses for Śuddha-Bhakti — <em>anyābhilāṣitā-śūnyam, jñāna-karmādy-anāvṛtam</em> — comes from Bhakti-rasāmṛta-sindhu 1.1.11. Śrīla Viśvanātha Cakravartī Ṭhākura quotes it directly, right after describing how a devotee\'s bhakti matures past the jñāna-miśra or karma-miśra stage:',
        },
        {
            type: 'text',
            content:
                '<div class="my-4 rounded-r-lg border-l-4 border-gray-400 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/30"><div class="mb-2 text-xs font-bold uppercase tracking-wide text-lightOrange">Commentary on SB 11.11.29-32</div><p class="italic text-black dark:text-gray-100">"The person who is śānta and has conquered the six urges is a perfect devotee, devoid of the desire for impersonal liberation. He may be called a devotee who is ātmārāma or a śānta-bhakta. Previously he was a jñāna-miśra-bhakta, and previous to that he was a karma-miśra-bhakta. When he attains perfection with bhakti as the main element, and without a covering of karma or jñāna, he is called a pure bhakta. The karma-miśra-bhakta is good, the jñāna-miśra-bhakta is better and the pure bhakta is the best (sattamaḥ)."</p></div>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhakti-rasāmṛta-sindhu 1.1.11 (quoted in the commentary on SB 11.11.32)',
                sloka: `anyābhilāṣitā-śūnyaṁ
jñāna-karmādy-anāvṛtam
ānukūlyena kṛṣṇānuśīlanaṁ
bhaktir uttamā`,
                translation:
                    'The highest <em>bhakti</em> (<strong>bhaktir uttamā</strong>) is defined as continuous service or emotions directed towards Kṛṣṇa, his expansion forms or others related to him (<strong>kṛṣṇānuśīlanaṁ</strong>), with a pleasing attitude towards Kṛṣṇa (<strong>ānukūlyena</strong>). It should be devoid of desires other than the desire to please the Lord (<strong>anyābhilāṣitā-śūnyaṁ</strong>), and unobstructed by impersonal <em>jñāna</em>, the materialistic rituals of <em>karma</em> or other unfavorable acts (<strong>jñāna-karmādy-anāvṛtam</strong>).',
            },
        },
    ],
};

export default content;
